import { useEffect, useState } from "react";
import { supportedLanguages } from "../../i18n";
import { getScope, normalizarId } from "../../hooks/useScope";
import { useTranslation } from "react-i18next"
import i18n from "../../i18n"
import {
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  SelectChangeEvent,
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Switch
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  ManageAccounts
} from "@mui/icons-material";
import api from "../../services/api";
import { useListaParceiros } from "../../hooks/useListaParceiros";
import { conferirGravacao } from "../../utils/conferirGravacao";
import { useCryptoNavigation } from "../../App";
import HorseLoader from "../../components/loadingHorse";
import AccessModal from "../../components/AccessModal";

// Interface para Role
interface Role {
  id?: number;
  roleId?: number;
  role_id?: number;
  name?: string;
  roleName?: string;
  role_name?: string;
}

// Interface para Tenant
interface Tenant {
  id?: number;
  tenantId?: number;
  tenant_id?: number;
  name?: string;
  tenantName?: string;
  tenant_name?: string;
  code?: string;
  tenantCode?: string;
  tenant_code?: string;
}

// Interface para dados do usuário atual (logado) - ✅ SIMPLIFICADO
interface CurrentUserData {
  isGeneralAdmin: boolean;
  userTenant: number | null;
  userRole: number; // ✅ ADICIONAR: Role do usuário logado
  userId: number | null; // id do usuário logado (1 = admin geral/raiz)
  /**
   * Parceiro de quem está cadastrando.
   * null = super admin · 0 = operação principal · >0 = parceiro terceiro.
   */
  userPartner: number | null;
}

// Interface para os dados do usuário do formulário - ✅ SIMPLIFICADO
interface UserData {
  userId?: number;
  userName?: string;
  userEmail?: string;
  userPhone?: string;
  userPass?: string;
  userRole?: number;
  userTenant?: number | null;
  userStatus?: number; // 1 = ativo, 0 = inativo
  /** Idioma do usuário — o nível mais específico da cascata. */
  userLanguage?: string | null;
  [key: string]: any;
}

// ✅ UTILITÁRIOS DE VALIDAÇÃO DE TELEFONE (internacional)
// Regras espelhadas do backend:
//  - Brasil (+55): +55 XX 9XXXXXXXX  → /^\+55 \d{2} 9\d{8}$/
//  - Demais países: +<código 1-3 dígitos> <número>  → /^\+\d{1,3} \d+$/
const BRAZIL_PHONE_REGEX = /^\+55 \d{2} 9\d{8}$/;
const INTL_PHONE_REGEX = /^\+\d{1,3} \d+$/;

const PhoneUtils = {
  validatePhone: (phone: string): boolean => {
    if (!phone || !phone.trim()) return false;
    const cleanPhone = phone.replace(/\s+/g, ' ').trim();
    if (cleanPhone.startsWith('+55')) {
      return BRAZIL_PHONE_REGEX.test(cleanPhone);
    }
    return INTL_PHONE_REGEX.test(cleanPhone);
  },

  formatPhone: (value: string): string => {
    if (!value) return value;

    // Mantém apenas "+", dígitos e espaços; colapsa espaços múltiplos.
    const sanitized = value.replace(/[^\d+\s]/g, '').replace(/\s+/g, ' ');
    const cleaned = sanitized.replace(/\s+/g, '');

    // Conveniência de máscara para o Brasil (+55 XX 9XXXXXXXX).
    if (cleaned.startsWith('+55')) {
      const numbers = cleaned.substring(3);
      if (numbers.length === 0) return '+55';
      if (numbers.length <= 2) return '+55 ' + numbers;
      return '+55 ' + numbers.substring(0, 2) + ' ' + numbers.substring(2, 11);
    }

    // 11 dígitos puros → assume Brasil (atalho comum).
    if (/^\d{11}$/.test(cleaned)) {
      return '+55 ' + cleaned.substring(0, 2) + ' ' + cleaned.substring(2);
    }

    // Demais países: não força máscara — preserva o que o usuário digitou
    // (apenas "+", dígitos e espaços), deixando a validação decidir.
    return sanitized.trimStart();
  },

  getErrorMessage: (phone: string): string => {
    if (!phone || !phone.trim()) return i18n.t('users.phoneRequired');
    const cleanPhone = phone.replace(/\s+/g, ' ').trim();
    if (cleanPhone.startsWith('+55')) {
      return i18n.t('users.phoneFormatBrazil');
    }
    return i18n.t('users.phoneFormatInternational');
  }
};

export default function UserForm() {
  const { t } = useTranslation()
  const { getParam, navigateToPage } = useCryptoNavigation();
  
  // Obter parâmetros da navegação criptografada
  const mode = getParam('mode') || 'new';
  const id = getParam('id');
  const openAccess = getParam('openAccess') === 'true';
  const isEditMode = mode === 'edit' && !!id;

  // ✅ Estado para controlar visibilidade da senha
  const [showPassword, setShowPassword] = useState(false);

  // ✅ Estados para o modal de gerenciamento de acessos
  const [openAccessModal, setOpenAccessModal] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);

  // ✅ IMPLEMENTAR: Obter dados reais do usuário atual
  const getCurrentUserData = (): CurrentUserData => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        
        console.log('👤 Dados do usuário logado:', user);
        
        // Tentar obter userRole de diferentes campos possíveis
        let userRole = 0;
        
        if (user.userRole !== undefined) {
          userRole = Number(user.userRole);
        } else if (user.roleId !== undefined) {
          userRole = Number(user.roleId);
        } else if (user.role_id !== undefined) {
          userRole = Number(user.role_id);
        } else if (user.Role !== undefined) {
          // Se Role for string "Admin", assumir que é userRole 1
          if (typeof user.Role === 'string' && user.Role.toLowerCase() === 'admin') {
            userRole = 1;
          } else if (typeof user.Role === 'number') {
            userRole = Number(user.Role);
          }
        }
        
        // ZERO preservado: ver normalizarId.
        const tenantNormalizado = normalizarId(user.userTenant);
        return {
          isGeneralAdmin: tenantNormalizado === null || user.isGeneralAdmin === true,
          userTenant: tenantNormalizado,
          userRole: userRole,
          userId: Number(user.userId ?? user.id ?? user.user_id) || null,
          // NULO é significativo: é o super admin. `?? 0` o confundiria com a
          // operação principal. Enquanto o backend não devolver o campo, o
          // valor também é nulo — e o usuário é tratado como super admin, que
          // é o comportamento de hoje.
          userPartner: normalizarId(user.userPartner)
        };
      }
    } catch (error) {
      console.error("Erro ao ler dados do localStorage:", error);
    }

    console.warn("Dados do usuário não encontrados, assumindo admin geral");
    return {
      isGeneralAdmin: true,
      userTenant: null,
      userRole: 0,
      userId: null,
      userPartner: null
    };
  };

  const userData = getCurrentUserData();

  // Só o grupo raiz escolhe o parceiro. Quem pertence a um parceiro TERCEIRO
  // não vê o campo: o vínculo dele é herdado e mover alguém para outro
  // parceiro não é decisão sua.
  //
  // NULO = super admin · ZERO = operação principal · >0 = parceiro terceiro.
  const podeEscolherParceiro =
    userData.userPartner === null || userData.userPartner === 0;
  const { parceiros, carregando: carregandoParceiros } =
    useListaParceiros(podeEscolherParceiro);
  
  console.log('🔐 Permissões do usuário:', {
    userRole: userData.userRole,
    canChangeStatus: userData.userRole === 1
  });

  // ✅ Verificar se o usuário logado pode alterar status
  const canChangeStatus = (): boolean => {
    return userData.userRole === 1;
  };

  // ✅ Obter configuração de acesso do Dashboard
  const getAccessConfig = () => {
    const accessType = (localStorage.getItem('accessType') as 'general' | 'specific') || 'general';
    const selectedTenantId = localStorage.getItem('selectedTenantId');
    
    console.log('📋 Configuração de acesso do Dashboard:', {
      accessType,
      selectedTenantId
    });
    
    return {
      accessType,
      selectedTenantId: selectedTenantId ? Number(selectedTenantId) : null
    };
  };

  const accessConfig = getAccessConfig();

  const [roles, setRoles] = useState<Role[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // ✅ SIMPLIFICADO: Estado inicial
  const [formData, setFormData] = useState<UserData>({
    userName: '',
    userEmail: '',
    userPhone: '',
    userPass: '',
    userRole: 0,
    userTenant: null,
    // Herda o parceiro de quem está criando. O grupo raiz pode trocar no
    // seletor; quem é de um parceiro fica com o dele.
    userPartner: userData.userPartner,
    userStatus: 1 // ✅ NOVO: Status padrão = ativo
  });
  
  const [originalData, setOriginalData] = useState<UserData>({});
  const [modifiedFields, setModifiedFields] = useState<Set<string>>(new Set());
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // ✅ Função para alternar visibilidade da senha
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // ✅ Função para abrir o modal de gerenciamento de acessos
  const handleOpenAccessModal = () => {
    setOpenAccessModal(true);
  };

  // ✅ SIMPLIFICADO: Funções auxiliares
  const isAdminOfTenant = (): boolean => {
    return !userData.isGeneralAdmin && userData.userTenant !== null;
  };

  const shouldShowTenantField = () => {
    return true;
  };

  // ✅ Verificar se o tenant está fixo (não pode ser alterado)
  const isTenantFixed = (): boolean => {
    // Fixo se for admin de tenant
    if (isAdminOfTenant()) {
      return true;
    }
    // Fixo se for admin geral mas está em modo específico
    if (userData.isGeneralAdmin && accessConfig.accessType === 'specific' && accessConfig.selectedTenantId) {
      return true;
    }
    return false;
  };

  const getAvailableTenants = (): Tenant[] => {
    if (!userData.isGeneralAdmin) return [];

    // Os proprietários seguem o parceiro ESCOLHIDO no formulário — é por isso
    // que o parceiro vem antes na tela. Quem não escolhe segue o parceiro
    // dele próprio.
    //
    // "Nenhum" não recorta: sem parceiro definido não há grupo a respeitar.
    // ZERO recorta normalmente, porque é um parceiro de verdade (grupo raiz).
    const parceiroAlvo = podeEscolherParceiro
      ? normalizarId(formData.userPartner)
      : userData.userPartner === 0
        ? null
        : userData.userPartner;

    if (parceiroAlvo === null) return tenants;

    // DEGRADAÇÃO: enquanto o backend não devolver `tenantPartnerId` no
    // proprietário, nenhum item teria o campo e o recorte esvaziaria a lista
    // inteira — ninguém conseguiria cadastrar. Por isso ele só entra quando ao
    // menos um proprietário traz o dado.
    const algumTemParceiro = tenants.some(
      (tnt) => normalizarId((tnt as any).tenantPartnerId) !== null,
    );
    if (!algumTemParceiro) return tenants;

    return tenants.filter(
      (tnt) => normalizarId((tnt as any).tenantPartnerId) === parceiroAlvo,
    );
  };

  // Funções para extrair dados de Role
  const getRoleId = (roleObj: Role): number => {
    return roleObj.roleId !== undefined ? roleObj.roleId : 
           roleObj.id !== undefined ? roleObj.id : 
           roleObj.role_id !== undefined ? roleObj.role_id : 0;
  };

  const getRoleName = (roleObj: Role): string => {
    return roleObj.roleName !== undefined ? roleObj.roleName : 
           roleObj.name !== undefined ? roleObj.name : 
           roleObj.role_name !== undefined ? roleObj.role_name : "Desconhecido";
  };

  // Funções para extrair dados de Tenant
  const getTenantId = (tenantObj: Tenant): number => {
    return tenantObj.tenantId !== undefined ? tenantObj.tenantId : 
           tenantObj.id !== undefined ? tenantObj.id : 
           tenantObj.tenant_id !== undefined ? tenantObj.tenant_id : 0;
  };

  const getTenantName = (tenantObj: Tenant): string => {
    let name = "";
    
    if (tenantObj.tenantName !== undefined) {
      name = tenantObj.tenantName;
    } else if (tenantObj.name !== undefined) {
      name = tenantObj.name;
    } else if (tenantObj.tenant_name !== undefined) {
      name = tenantObj.tenant_name;
    } else {
      name = "Desconhecido";
    }
    
    name = String(name)
      .replace(/\s*\(ID:\s*\d+\)\s*$/i, '')
      .replace(/\s*-\s*\d+\s*$/, '')
      .replace(/^\d+\s*-\s*/, '')
      .replace(/\s*\|\s*\d+\s*$/, '')
      .replace(/^\d+\s*\|\s*/, '')
      .trim();
    
    return name;
  };

  const getTenantCode = (tenantObj: Tenant): string => {
    return tenantObj.tenantCode !== undefined ? tenantObj.tenantCode : 
           tenantObj.code !== undefined ? tenantObj.code : 
           tenantObj.tenant_code !== undefined ? tenantObj.tenant_code : "";
  };

  // Carregar roles
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        console.log("📊 Carregando roles...");
        const roleResponse = await api.get("/role");
        
        let rolesData: Role[] = [];
        
        if (Array.isArray(roleResponse.data)) {
          rolesData = roleResponse.data;
        } else if (roleResponse.data && Array.isArray(roleResponse.data.roles)) {
          rolesData = roleResponse.data.roles;
        } else if (roleResponse.data && Array.isArray(roleResponse.data.role)) {
          rolesData = roleResponse.data.role;
        } else if (roleResponse.data && typeof roleResponse.data === 'object') {
          const possibleArrayKeys = Object.keys(roleResponse.data).filter(key => 
            Array.isArray(roleResponse.data[key]) && roleResponse.data[key].length > 0
          );
          
          if (possibleArrayKeys.length > 0) {
            const preferredKey = possibleArrayKeys.find(key => 
              key.toLowerCase() === 'roles' || key.toLowerCase() === 'role'
            ) || possibleArrayKeys[0];
            
            const arrayData = roleResponse.data[preferredKey];
            rolesData = arrayData.map((item: any): Role => ({
              roleId: item.roleId !== undefined ? item.roleId : item.id,
              roleName: item.roleName !== undefined ? item.roleName : item.name
            }));
          }
        }
        
        console.log(`✅ Encontrados ${rolesData.length} perfis:`, rolesData);
        setRoles(rolesData);
      } catch (error) {
        console.error("❌ Erro ao carregar perfis:", error);
        setError(t("users.loadProfilesError"));
      }
    };

    fetchRoles();
  }, []);

  // Carregar tenants
  useEffect(() => {
    const fetchTenants = async () => {
      try {
        console.log("🏢 Carregando tenants...");
        const tenantResponse = await api.get("/tenant");
        
        let tenantsData: Tenant[] = [];
        
        if (Array.isArray(tenantResponse.data)) {
          tenantsData = tenantResponse.data;
        } else if (tenantResponse.data && Array.isArray(tenantResponse.data.tenants)) {
          tenantsData = tenantResponse.data.tenants;
        } else if (tenantResponse.data && Array.isArray(tenantResponse.data.tenant)) {
          tenantsData = tenantResponse.data.tenant;
        } else if (tenantResponse.data && typeof tenantResponse.data === 'object') {
          const possibleArrayKeys = Object.keys(tenantResponse.data).filter(key => 
            Array.isArray(tenantResponse.data[key]) && tenantResponse.data[key].length > 0
          );
          
          if (possibleArrayKeys.length > 0) {
            const preferredKey = possibleArrayKeys.find(key => 
              key.toLowerCase().includes('tenant')
            ) || possibleArrayKeys[0];
            
            const arrayData = tenantResponse.data[preferredKey];
            tenantsData = arrayData.map((item: any): Tenant => ({
              tenantId: item.tenantId !== undefined ? item.tenantId : item.id,
              tenantName: item.tenantName !== undefined ? item.tenantName : item.name,
              tenantCode: item.tenantCode !== undefined ? item.tenantCode : item.code
            }));
          }
        }
        
        console.log(`✅ Encontrados ${tenantsData.length} tenants:`, tenantsData);
        setTenants(tenantsData);
      } catch (error) {
        console.error("❌ Erro ao carregar tenants:", error);
        setError(t("users.loadTenantsError"));
      }
    };

    fetchTenants();
  }, []);

  // Carregar dados do usuário se estiver em modo de edição
  useEffect(() => {
    if (isEditMode && id) {
      const fetchUser = async () => {
        try {
          setLoading(true);
          setError(null);
          console.log(`🔍 Buscando dados do usuário com ID: ${id}`);
          
          const response = await api.get(`/user/${id}`);
          const userData = response.data.user || response.data;
          
          if (userData) {
            const transformedData = {
              userId: userData.userId,
              userName: userData.userName || "",
              userEmail: userData.userEmail || "",
              userPhone: userData.userPhone || "",
              userPass: "",
              userRole: userData.userRole !== undefined ? userData.userRole :
                       userData.roleId !== undefined ? userData.roleId :
                       userData.role_id !== undefined ? userData.role_id : 0,
              // `userData.userTenant ? ... : null` descartava o proprietário
              // ZERO, que é um proprietário de verdade.
              userTenant: normalizarId(userData.userTenant),
              userStatus: userData.userStatus !== undefined ? userData.userStatus : 1,
              // Precisa estar AQUI: a whitelist descarta campo não listado, e
              // sem ele em originalData o comparador de alterações marcaria o
              // idioma como modificado para sempre.
              userLanguage: userData.userLanguage ?? null,
              // ZERO é o grupo raiz, não ausência.
              userPartner: normalizarId(userData.userPartner)
            };
            
            console.log("✅ Dados do usuário carregados:", transformedData);
            setFormData(transformedData);
            setOriginalData(transformedData);
            setModifiedFields(new Set());
          }
        } catch (err: any) {
          console.error("❌ Erro ao carregar usuário:", err);
          const errorMessage = err.response?.data?.message || err.message || "Erro desconhecido";
          setError(`Não foi possível carregar os dados: ${errorMessage}`);
        } finally {
          setLoading(false);
        }
      };

      fetchUser();
    }
  }, [isEditMode, id]);

  // ✅ Auto-selecionar tenant (do admin ou do Dashboard)
  useEffect(() => {
    if (!isEditMode) {
      if (isAdminOfTenant()) {
        console.log("🏢 Admin de tenant - Selecionando tenant:", userData.userTenant);
        
        setFormData(prev => ({
          ...prev,
          userTenant: userData.userTenant
        }));
        
        setModifiedFields(prev => new Set([...prev, 'userTenant']));
      }
      else if (accessConfig.accessType === 'specific' && accessConfig.selectedTenantId) {
        console.log("🏢 Acesso específico - Selecionando tenant do Dashboard:", accessConfig.selectedTenantId);
        
        setFormData(prev => ({
          ...prev,
          userTenant: accessConfig.selectedTenantId
        }));
        
        setModifiedFields(prev => new Set([...prev, 'userTenant']));
      }
    }
  }, [isEditMode, accessConfig.accessType, accessConfig.selectedTenantId]);

  // Abrir modal automaticamente quando parâmetro openAccess=true
  useEffect(() => {
    if (isEditMode && openAccess && formData.userId && !loading && !openAccessModal && !hasAutoOpened) {
      console.log("🔓 Abrindo modal de acessos automaticamente...");
      const timer = setTimeout(() => {
        setOpenAccessModal(true);
        setHasAutoOpened(true);
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [isEditMode, openAccess, formData.userId, loading, openAccessModal, hasAutoOpened]);

  const hasValueChanged = (originalValue: any, currentValue: any): boolean => {
    if (originalValue === currentValue) return false;
    if (originalValue == null && currentValue == null) return false;
    if (originalValue == null || currentValue == null) return true;
    
    if (typeof originalValue === 'number' || typeof currentValue === 'number') {
      return Number(originalValue) !== Number(currentValue);
    }
    
    return String(originalValue) !== String(currentValue);
  };

  const getModifiedData = (): Record<string, any> => {
    const modifiedData: Record<string, any> = {};
    
    if (formData.userId) {
      modifiedData.userId = formData.userId;
    }
    
    modifiedFields.forEach(fieldName => {
      if (fieldName === 'userPass' && (!formData.userPass || formData.userPass.trim() === '')) {
        return;
      }
      
      if (hasValueChanged(originalData[fieldName], formData[fieldName])) {
        modifiedData[fieldName] = formData[fieldName];
      }
    });
    
    return modifiedData;
  };

  const handleChange = (fieldName: string, fieldValue: any) => {
    console.log(`🔄 Campo alterado: ${fieldName} = ${fieldValue}`);
    
    let processedValue = fieldValue;
    
    if (fieldName === 'userPhone') {
      processedValue = PhoneUtils.formatPhone(fieldValue);
    }
    
    if (fieldName === 'userTenant') {
      // `fieldValue ? ... : null` descartava o proprietário ZERO.
      processedValue = normalizarId(fieldValue);
    }
    
    if (fieldName === 'userStatus') {
      processedValue = fieldValue ? 1 : 0;
    }
    
    setFormData(prev => ({
      ...prev,
      [fieldName]: processedValue,
    }));
    
    setModifiedFields(prev => new Set([...prev, fieldName]));
    
    if (validationErrors[fieldName]) {
      setValidationErrors(prev => ({
        ...prev,
        [fieldName]: '',
      }));
    }
  };

  const validateForm = (data: UserData, isEditMode: boolean): Record<string, string> => {
    const errors: Record<string, string> = {};
    
    if (!data.userName?.trim()) {
      errors.userName = t("users.nameRequired");
    }

    if (!data.userEmail?.trim()) {
      errors.userEmail = t("users.emailRequired");
    } else if (!/\S+@\S+\.\S+/.test(data.userEmail)) {
      errors.userEmail = t("users.emailInvalid");
    }

    if (!data.userPhone || !data.userPhone.trim()) {
      errors.userPhone = t("users.phoneRequired");
    } else if (!PhoneUtils.validatePhone(data.userPhone)) {
      errors.userPhone = PhoneUtils.getErrorMessage(data.userPhone);
    }

    if (!isEditMode) {
      if (!data.userPass) {
        errors.userPass = t("users.passwordRequired");
      } else if (data.userPass.length < 6) {
        errors.userPass = t("users.passwordMinLength");
      }
    } else {
      if (data.userPass && data.userPass.length < 6) {
        errors.userPass = t("users.passwordMinLength");
      }
    }

    if (!data.userRole || data.userRole === 0) {
      errors.userRole = t("users.profileRequired");
    }

    // Admin (userRole 1) SEM proprietário associado é o nível mais alto: ele
    // enxerga tudo. Só quem já está nesse nível, e no grupo raiz, pode criar
    // outro igual — um admin de parceiro cria apenas admin COM proprietário,
    // senão criaria um par seu capaz de sair do próprio parceiro.
    //
    // A regra antiga era `userData.userId !== 1`: amarrada a um id fixo, ela
    // dava o poder a uma linha do banco em vez de a um papel.
    // Delegado ao resolvedor de escopo, que já NEGA quando o parceiro é
    // desconhecido — criar admin sem proprietário é poder administrativo, e
    // poder não pode nascer de um campo que faltou na resposta.
    const criadorPodeCriarAdminGlobal = getScope().canCreateUnscopedAdmin;

    // `!data.userTenant` trataria o proprietário ZERO como ausente.
    const semProprietario =
      data.userTenant === null ||
      data.userTenant === undefined ||
      String(data.userTenant) === '';

    if (data.userRole === 1 && semProprietario && !criadorPodeCriarAdminGlobal) {
      errors.userTenant = t("users.tenantRequiredForAdmin");
    }

    return errors;
  };

  const transformBeforeSave = (data: UserData): any => {
    const userDataToSave: any = { ...data };

    // NA EDIÇÃO ESTA FUNÇÃO RECEBE SÓ OS CAMPOS ALTERADOS.
    //
    // Por isso nada aqui pode FABRICAR valor para campo ausente: o que entrar
    // vira PUT, e um campo que o usuário nem tocou volta ao servidor com o
    // valor inventado aqui — apagando o que estava gravado. Era o que fazia
    // trocar só o perfil limpar o parceiro (e também o telefone e o
    // proprietário).
    const presente = (campo: string) =>
      Object.prototype.hasOwnProperty.call(userDataToSave, campo);

    // Vínculo com o parceiro.
    //
    // Na CRIAÇÃO: o grupo raiz manda o que escolheu; quem pertence a um
    // parceiro passa o dele adiante (o front preenche; a API é que precisa
    // RECUSAR outro valor — isto é conveniência, não barreira).
    //
    // Na EDIÇÃO: só vai se o seletor foi mexido, pelo mecanismo de campos
    // alterados. Quem não pode escolher nem tem o seletor na tela.
    //
    // NULO = sem vínculo · ZERO = grupo raiz. Nada de valor-verdade.
    if (!isEditMode) {
      userDataToSave.userPartner = podeEscolherParceiro
        ? data.userPartner ?? null
        : userData.userPartner;
    } else if (!podeEscolherParceiro) {
      delete userDataToSave.userPartner;
    }
    // O nome vem derivado do id pelo servidor — não é nosso para escrever.
    delete userDataToSave.userPartnerName;
    
    console.log("🔄 Transformando dados:", data);
    
    // Só normaliza o que ESTÁ no pedido. Ausente continua ausente.
    if (presente("userPhone")) {
      const tel = userDataToSave.userPhone;
      userDataToSave.userPhone = !tel || tel.trim() === "" ? "" : tel;
    }
    
    if (userDataToSave.userRole !== undefined) {
      userDataToSave.userRole = Number(userDataToSave.userRole);
    }
    
    if (presente("userTenant")) {
      // NULO aqui é escolha explícita de "sem proprietário" — o campo só
      // chega até aqui quando foi mexido de verdade.
      userDataToSave.userTenant =
        userDataToSave.userTenant === null || userDataToSave.userTenant === ""
          ? null
          : Number(userDataToSave.userTenant);
    }
    
    if (userDataToSave.userStatus !== undefined) {
      userDataToSave.userStatus = Number(userDataToSave.userStatus);
    }
    
    console.log("🔄 userTenant final:", userDataToSave.userTenant, typeof userDataToSave.userTenant);
    console.log("🔄 userStatus final:", userDataToSave.userStatus, typeof userDataToSave.userStatus);
    
    if (userDataToSave.userName) {
      userDataToSave.name = userDataToSave.userName;
    }
    
    if (isEditMode && (!userDataToSave.userPass || userDataToSave.userPass.trim() === "")) {
      delete userDataToSave.userPass;
    }
    
    console.log("🔄 Dados finais:", userDataToSave);
    return userDataToSave;
  };

  /**
   * Campos cuja gravação silenciosa importa. Senha fica de fora de propósito:
   * não volta na leitura, e compará-la acusaria o servidor à toa.
   */
  const rotuloDoCampo = (): Record<string, string> => ({
    userLanguage: t("common.language"),
    userPartner: t("fields.partner"),
    userTenant: t("fields.owner"),
    userRole: t("fields.profile"),
    userStatus: t("fields.status"),
  });

  /**
   * Relê o usuário e avisa se o servidor aceitou sem gravar. Devolve true
   * quando algo não foi gravado — aí a tela NÃO navega, senão a lista
   * mostraria o valor antigo e pareceria bug da tela.
   */
  const avisarSeNaoGravou = async (
    userIdSalvo: number | string,
    enviado: any,
  ): Promise<boolean> => {
    const rotulos = rotuloDoCampo();
    const esperado: Record<string, unknown> = {};
    for (const campo of Object.keys(rotulos)) {
      if (Object.prototype.hasOwnProperty.call(enviado, campo)) {
        esperado[campo] = enviado[campo];
      }
    }
    if (Object.keys(esperado).length === 0) return false;

    const divergentes = await conferirGravacao(async () => {
      const resp = await api.get(`/user/${userIdSalvo}`);
      return resp.data?.user ?? resp.data;
    }, esperado);

    if (divergentes.length === 0) return false;

    setError(
      t("form.acceptedNotStored", {
        fields: divergentes.map((c) => rotulos[c] ?? c).join(", "),
      }),
    );
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log("🚀 Iniciando handleSubmit");
    
    try {
      setLoading(true);
      setError(null);
      setValidationErrors({});
      
      let dataToSave: any;
      
      if (isEditMode) {
        const modifiedData = getModifiedData();
        
        const fieldsCount = Object.keys(modifiedData).filter(key => key !== 'userId').length;
        if (fieldsCount === 0) {
          setError(t("form.noChanges"));
          setLoading(false);
          return;
        }
        
        const errors = validateForm(formData, isEditMode);
        // Em edição, valida SÓ os campos efetivamente alterados: não bloqueia o
        // salvamento por dado pré-existente inválido em campos intocados (ex.:
        // e-mail proposital antigo ao mudar só o telefone) e evita erros em
        // campos não visíveis que gerariam "corrija os erros" sem apontar onde.
        const modifiedKeys = new Set(Object.keys(modifiedData));
        // Se o perfil mudou para admin, o tenant vira obrigatório mesmo sem tocado.
        if (modifiedKeys.has("userRole")) modifiedKeys.add("userTenant");
        Object.keys(errors).forEach((key) => {
          if (!modifiedKeys.has(key)) delete errors[key];
        });
        if (Object.keys(errors).length > 0) {
          setValidationErrors(errors);
          setError(t("form.fixErrors"));
          setLoading(false);
          return;
        }

        dataToSave = transformBeforeSave(modifiedData);
      } else {
        const errors = validateForm(formData, false);
        if (Object.keys(errors).length > 0) {
          setValidationErrors(errors);
          setError(t("form.fixErrors"));
          setLoading(false);
          return;
        }
        
        dataToSave = transformBeforeSave(formData);
      }
      
      if (isEditMode) {
        console.log("🚀 Atualizando usuário:", dataToSave);
        await api.put(`/user/${id}`, dataToSave);
        if (await avisarSeNaoGravou(id as string, dataToSave)) {
          setLoading(false);
          return;
        }
        console.log("✅ Sucesso! Retornando à lista");
        navigateToPage('usuarios', { mode: 'list' });
      } else {
        console.log("🚀 Criando usuário:", dataToSave);
        const createResponse = await api.post(`/user`, dataToSave);
        
        let newUserId = createResponse.data.Id ||
                        createResponse.data.userId || 
                        createResponse.data.user?.userId || 
                        createResponse.data.id ||
                        createResponse.data.insertId ||
                        createResponse.data.user_id ||
                        createResponse.data.user?.id ||
                        createResponse.data.data?.userId ||
                        createResponse.data.data?.id;
        
        console.log("✅ Usuário criado com ID:", newUserId);
        
        if (!newUserId && dataToSave.userEmail) {
          console.log("⚠️ ID não encontrado na resposta. Tentando buscar pelo email...");
          try {
            const searchResponse = await api.get(`/user`);
            const users = searchResponse.data.users || searchResponse.data.user || searchResponse.data || [];
            
            const createdUser = Array.isArray(users) 
              ? users.find((u: any) => u.userEmail === dataToSave.userEmail || u.email === dataToSave.userEmail)
              : null;
            
            if (createdUser) {
              newUserId = createdUser.userId || createdUser.id || createdUser.user_id;
              console.log("✅ ID encontrado via busca pelo email:", newUserId);
            }
          } catch (searchError) {
            console.error("❌ Erro ao buscar usuário pelo email:", searchError);
          }
        }
        
        if (newUserId && (await avisarSeNaoGravou(newUserId, dataToSave))) {
          // Criado, mas com campo ignorado. Leva para a edição assim mesmo —
          // o registro existe — e o aviso explica o que não entrou.
          navigateToPage('usuarios', { mode: 'edit', id: newUserId.toString() });
          setLoading(false);
          return;
        }

        if (newUserId) {
          console.log("➡️ Redirecionando para edição com modal de acessos...");
          navigateToPage('usuarios', { 
            mode: 'edit', 
            id: newUserId.toString(),
            openAccess: 'true'
          });
        } else {
          console.error("❌ ERRO: Não foi possível obter o ID do usuário criado!");
          alert("Usuário criado com sucesso, mas não foi possível abrir o gerenciamento de acessos automaticamente.\n\nPor favor, encontre o usuário na lista e clique em 'Editar' para gerenciar os acessos.");
          navigateToPage('usuarios', { mode: 'list' });
        }
      }
      
    } catch (err: any) {
      console.error("❌ Erro ao salvar:", err);
      let errorMessage = err.response?.data?.message || err.message || "Erro desconhecido";
      
      if (errorMessage.includes("User Tenant is not valid")) {
        errorMessage = t("users.invalidTenant", { tenant: formData.userTenant });
      }
      
      setError(`Não foi possível salvar: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    console.log("❌ Cancelando e voltando à lista");
    navigateToPage('usuarios', { mode: 'list' });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
        <HorseLoader />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {isEditMode ? '' : 'Salvando...'}
        </Typography>
      </Box>
    );
  }

  const availableTenants = getAvailableTenants();

  return (
    <>
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          {isEditMode ? t("users.editTitle") : t("users.newTitle")}
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <form onSubmit={handleSubmit} noValidate>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Nome */}
            <TextField
              fullWidth
              label={t("fields.name")}
              name="userName"
              type="text"
              required
              value={formData.userName || ""}
              onChange={(e) => handleChange('userName', e.target.value)}
              error={!!validationErrors.userName}
              helperText={validationErrors.userName}
              disabled={loading}
            />

            {/* Email */}
            <TextField
              fullWidth
              label={t("fields.email")}
              name="userEmail"
              type="email"
              required
              value={formData.userEmail || ""}
              onChange={(e) => handleChange('userEmail', e.target.value)}
              error={!!validationErrors.userEmail}
              helperText={validationErrors.userEmail}
              disabled={loading}
            />

            {/* Telefone */}
            <TextField
              fullWidth
              label={t("fields.phone")}
              name="userPhone"
              required
              value={formData.userPhone || ''}
              onChange={(e) => handleChange('userPhone', e.target.value)}
              error={!!validationErrors.userPhone}
              helperText={validationErrors.userPhone || t("users.phoneFormatHelper")}
              placeholder="+55 11 999887766"
              inputProps={{
                maxLength: 16
              }}
              disabled={loading}
            />

            {/* Senha com ícone de visualização */}
            <TextField
              fullWidth
              label={isEditMode ? t("users.passwordEditLabel") : t("fields.password")}
              name="userPass"
              type={showPassword ? "text" : "password"}
              required={!isEditMode}
              value={formData.userPass || ""}
              onChange={(e) => handleChange('userPass', e.target.value)}
              error={!!validationErrors.userPass}
              helperText={validationErrors.userPass}
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Perfil */}
            <FormControl fullWidth error={!!validationErrors.userRole}>
              <InputLabel id="userRole-label">{t("fields.profile")}</InputLabel>
              <Select
                labelId="userRole-label"
                name="userRole"
                value={formData.userRole || 0}
                required
                onChange={(e: SelectChangeEvent<any>) => handleChange('userRole', e.target.value)}
                label={t("fields.profile")}
                disabled={loading}
              >
                <MenuItem value={0} disabled>
                  Selecione um perfil
                </MenuItem>
                {roles.map((role) => (
                  <MenuItem key={getRoleId(role)} value={getRoleId(role)}>
                    {getRoleName(role)}
                  </MenuItem>
                ))}
              </Select>
              {validationErrors.userRole && (
                <FormHelperText>{validationErrors.userRole}</FormHelperText>
              )}
            </FormControl>

            {/* ✅ NOVO: Campo Status - Apenas em modo de edição e para userRole === 1 */}
            {isEditMode && canChangeStatus() && (
              <Box sx={{
                p: 2,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'action.hover'
              }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.userStatus === 1}
                      onChange={(e) => handleChange('userStatus', e.target.checked)}
                      color="success"
                      disabled={loading}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1">
                        Status do Usuário
                      </Typography>
                      <Chip 
                        label={formData.userStatus === 1 ? t("common.active") : t("common.inactive")}
                        size="small"
                        color={formData.userStatus === 1 ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </Box>
                  }
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, ml: 4 }}>
                  {formData.userStatus === 1 
                    ? t('users.statusActiveHelper')
                    : t('users.statusInactiveHelper')}
                </Typography>
              </Box>
            )}

            {/* Idioma do usuário: vence o do proprietário e o do parceiro. */}
            <FormControl fullWidth>
              <InputLabel id="userLanguage-label">{t("common.language")}</InputLabel>
              <Select
                labelId="userLanguage-label"
                label={t("common.language")}
                value={(formData.userLanguage as string) ?? ''}
                onChange={(e) => handleChange('userLanguage', e.target.value === '' ? null : String(e.target.value))}
              >
                <MenuItem value=""><em>—</em></MenuItem>
                {supportedLanguages.map(l => (
                  <MenuItem key={l.code} value={l.code}>{l.name}</MenuItem>
                ))}
              </Select>
              <FormHelperText>{t("users.languageHelper")}</FormHelperText>
            </FormControl>

            {/* Campo Tenants - Condicional baseado no tipo de admin */}
            {!isEditMode && (
              <>
            {podeEscolherParceiro && (
              <FormControl fullWidth margin="normal" disabled={loading || carregandoParceiros}>
                <InputLabel id="userPartner-label">{t("fields.partner")}</InputLabel>
                <Select
                  labelId="userPartner-label"
                  name="userPartner"
                  value={
                    formData.userPartner === null || formData.userPartner === undefined
                      ? ''
                      : String(formData.userPartner)
                  }
                  onChange={(e: SelectChangeEvent<any>) => {
                    const valor = e.target.value;
                    // Vazio é "sem vínculo"; "0" é o grupo raiz.
                    const novoParceiro = valor === '' ? null : Number(valor);
                    handleChange('userPartner', novoParceiro);

                    // O proprietário escolhido pode não pertencer ao parceiro
                    // novo. Mantê-lo deixaria o formulário incoerente, e o
                    // campo mostraria um valor que nem está mais na lista.
                    const tenantAtual = normalizarId(formData.userTenant);
                    if (tenantAtual === null || novoParceiro === null) return;
                    const continuaValido = tenants.some(
                      (tnt) =>
                        normalizarId(getTenantId(tnt)) === tenantAtual &&
                        normalizarId((tnt as any).tenantPartnerId) === novoParceiro,
                    );
                    if (!continuaValido) handleChange('userTenant', null);
                  }}
                  input={<OutlinedInput label={t("fields.partner")} />}
                >
                  <MenuItem value=""><em>{t("partners.noneOption")}</em></MenuItem>
                  {parceiros.map((pc) => (
                    <MenuItem key={pc.partnerId} value={String(pc.partnerId)}>
                      {pc.partnerName || `#${pc.partnerId}`}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{t("partners.selectHelper")}</FormHelperText>
              </FormControl>
            )}

            {shouldShowTenantField() && (
              <FormControl fullWidth error={!!validationErrors.userTenant}>
                
                {/* ✅ TENANT FIXO: Admin de tenant OU Admin geral em modo específico */}
                {isTenantFixed() ? (
                  <>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Proprietário Associado
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '56px' }}>
                      <Chip
                        label={(() => {
                          const tenantId = formData.userTenant || userData.userTenant || accessConfig.selectedTenantId;
                          const tenant = tenants.find(t => getTenantId(t) === tenantId);
                          return tenant ? getTenantName(tenant) : `Proprietário ${tenantId}`;
                        })()}
                        color="primary"
                        variant="filled"
                        sx={{ 
                          height: '40px',
                          fontSize: '0.95rem',
                          px: 2
                        }}
                      />
                    </Box>
                      
                    <FormHelperText>
                      🔒 {isAdminOfTenant() 
                        ? t("users.tenantAutoAssociated")
                        : t("users.tenantSystemAssociated")}
                    </FormHelperText>
                  </>
                ) : (
                  /* ✅ ADMIN GERAL EM MODO GERAL: Select livre para escolher tenant */
                  <>
                    <InputLabel id="userTenant-label">{t("fields.owner")}</InputLabel>
                    <Select
                      labelId="userTenant-label"
                      name="userTenant"
                      value={formData.userTenant || ''}
                      onChange={(e: SelectChangeEvent<any>) => {
                        const value = e.target.value;
                        handleChange('userTenant', value ? Number(value) : null);
                      }}
                      input={<OutlinedInput label={t("fields.owner")} />}
                      disabled={loading}
                    >
                      <MenuItem value="">
                        <em>{t("users.adminGeneral")}</em>
                      </MenuItem>
                      {availableTenants.map((tenant) => (
                        <MenuItem key={getTenantId(tenant)} value={getTenantId(tenant)}>
                          <ListItemText 
                            primary={getTenantName(tenant)} 
                            secondary={getTenantCode(tenant) ? `Código: ${getTenantCode(tenant)}` : undefined}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {validationErrors.userTenant || t("users.selectOwnerHelper")}
                    </FormHelperText>
                  </>
                )}
              </FormControl>
            )}

            </>
          )}
          </Box>
          
          <Box sx={{ mt: 4, position: 'relative' }}>
            <Box sx={{ display: 'flex', justifyContent: isEditMode ? 'space-between' : 'flex-end', alignItems: 'center', gap: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              {/* Botão Gerenciar Acessos - APENAS em modo de edição */}
              {isEditMode && (
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<ManageAccounts />}
                  onClick={handleOpenAccessModal}
                  disabled={loading}
                  sx={{ 
                    minWidth: 180,
                    height: 42,
                    borderWidth: 2,
                    fontWeight: 500,
                    '&:hover': {
                      borderWidth: 2,
                      backgroundColor: 'rgba(156, 39, 176, 0.04)'
                    }
                  }}
                >
                  {t('access.title')}
                </Button>
              )}
              
              {/* Botões de ação à direita */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button 
                  onClick={handleCancel}
                  variant="outlined" 
                  disabled={loading}
                  sx={{ 
                    minWidth: 120,
                    height: 42,
                    fontWeight: 500
                  }}
                >
                  Cancelar
                </Button>
                
                <Button 
                  type="submit" 
                  variant="contained" 
                  color="primary"
                  disabled={loading}
                  sx={{ 
                    minWidth: 120,
                    height: 42,
                    fontWeight: 600,
                    boxShadow: 2,
                    '&:hover': {
                      boxShadow: 4
                    }
                  }}
                >
                  {loading ? 'Salvando...' : (isEditMode ? "Atualizar" : "Salvar")}
                </Button>
              </Box>
            </Box>
          </Box>
        </form>
      </Paper>

      {/* Modal de Gerenciamento de Acessos */}
      {isEditMode && formData.userId && (
        <AccessModal
          open={openAccessModal}
          onClose={() => {
            setOpenAccessModal(false);
            // Veio da CRIAÇÃO (openAccess=true): o formulário atrás é só o
            // caminho até aqui, não uma edição que alguém pediu. Deixar a
            // pessoa nele obriga a "cancelar" ou "atualizar" dados que ela
            // não mudou — e "cancelar" parece desfazer o usuário recém-criado.
            if (openAccess) navigateToPage('usuarios', { mode: 'list' });
          }}
          userId={formData.userId}
        />
      )}
    </>
  );
}